import { useCallback } from 'react';

import { snackbar } from '@occc-app/design-system';
import { Audience_Status, OfferFragment } from '@occc/graphql';
import { offerDuplicationService } from '@occc-app/offerDuplicationService';
import { useChainId } from '@occc-app/Chain/hooks';
import { PreparedConstraints } from '@occc-app/routes/CampaignComponents/component-details';

import { getOfferDuplicationOperations } from '../operations/getOfferDuplicationOperations';
import { useGetOfferOperationHandlers } from '../operations/useGetOfferOperationHandlers';
import { runOperations } from '../operations/runOperations';
import { Operation } from '../types';

export type CopyOffer = (props: {
  offer: OfferFragment;
  campaignUuid?: string;
  constraints?: PreparedConstraints;
}) => Promise<string | undefined>;

export const useCopyOffer = (): CopyOffer => {
  const chainId = useChainId();
  const operationHandlers = useGetOfferOperationHandlers();

  const copyOffer: CopyOffer = useCallback(
    async ({ offer, campaignUuid, constraints }) => {
      try {
        const inputs = offerDuplicationService.copy(
          { ...offer, earningMechanism: offer.earningMechanism },
          chainId,
          campaignUuid,
        );

        const offerCreateOperation: Operation = {
          name: 'offerCreate',
          variables: {
            input: inputs.offerCreateInput,
          },
        };
        const offerCreateHandler = operationHandlers['offerCreate'];
        const offerResponse = await offerCreateHandler({
          variables: offerCreateOperation.variables,
        });

        const offerId = offerResponse.data!.offerCreate.id;
        const hasAudienceConstraintInNonActiveStatus =
          constraints?.inAudience && constraints.inAudience.status !== Audience_Status.Active;

        const finalInputs = {
          ...inputs,
          constraintInputs: hasAudienceConstraintInNonActiveStatus
            ? inputs.constraintInputs.filter((constraint) => !constraint.inAudience)
            : inputs.constraintInputs,
        };

        const operations: Operation[] = getOfferDuplicationOperations(offerId, finalInputs);
        await runOperations(operations, operationHandlers);

        if (hasAudienceConstraintInNonActiveStatus) {
          snackbar.warning(
            `"${offer.name}" has been duplicated without the audience constraint since the audience is currently inactive`,
          );
        } else {
          snackbar.success(`"${offer.name}" has been successfully duplicated`);
        }

        return offerId;
      } catch (e) {
        snackbar.error(e.message);
        return;
      }
    },
    [chainId, operationHandlers],
  );

  return copyOffer;
};
